import React from 'react';
import { X, AlertCircle, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SubscriptionPlan } from '../services/subscriptionService';

interface UsageLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  remainingUploads: number;
  isAdmin: boolean;
  currentPlan?: SubscriptionPlan;
}

export function UsageLimitModal({ 
  isOpen, 
  onClose, 
  remainingUploads, 
  isAdmin,
  currentPlan = SubscriptionPlan.FREE 
}: UsageLimitModalProps) {
  if (!isOpen) return null;
  
  // Calculate renewal date (first day of next month)
  const today = new Date();
  const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const formattedRenewalDate = nextMonth.toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-center">
          <div className={`w-16 h-16 ${remainingUploads === 0 ? 'bg-red-100' : 'bg-amber-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
            {remainingUploads === 0 ? (
              <AlertCircle className="w-8 h-8 text-red-600" />
            ) : (
              <AlertTriangle className="w-8 h-8 text-amber-600" />
            )}
          </div>

          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {remainingUploads === 0 ? 'Usage Limit Reached' : 'Usage Limit Warning'}
          </h3>

          <div className="mt-4 text-gray-700">
            {remainingUploads === 0 ? (
              <>
                <p className="mb-4">
                  You've used all of your monthly resume analyses on your 
                  <span className="font-semibold"> {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)} Plan</span>.
                </p>
                <div className="text-sm bg-blue-50 p-4 rounded-lg mb-4">
                  <p className="mb-2 font-medium">Your options:</p>
                  <ul className="text-left list-disc pl-5 space-y-1">
                    <li>Wait until <span className="font-medium">{formattedRenewalDate}</span> when your plan renews</li>
                    <li>Upgrade to a higher plan with more monthly analyses</li>
                  </ul>
                </div>
              </>
            ) : (
              <>
                <p className="mb-3">
                  You have <span className="font-semibold text-amber-600">{remainingUploads}</span> resume {remainingUploads === 1 ? 'analysis' : 'analyses'} remaining
                  on your <span className="font-medium">{currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)} Plan</span>.
                </p>
                <p className="text-sm bg-amber-50 p-4 rounded-lg">
                  Your plan will renew on <span className="font-medium">{formattedRenewalDate}</span>, or you can upgrade now to get more analyses.
                </p>
              </>
            )}
          </div>

          <div className="mt-6 flex justify-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500 border border-gray-300 rounded-md"
            >
              Close
            </button>
            <Link
              to="/pricing"
              className={`px-4 py-2 text-sm font-medium text-white ${remainingUploads === 0 ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'} rounded-md`}
              onClick={onClose}
            >
              View Pricing Plans
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
} 